import React from 'react';

/**
 * React.lazy() wrapper that recovers from stale-deploy chunk-load failures.
 *
 * When a CloudFront/S3 deploy ships a new bundle, any tab that already
 * downloaded `index.html` for the previous build still holds references to
 * the old hashed chunk URLs (e.g. `SignupPage-vDpAc7hm.js`). When React
 * later calls the lazy factory, the dynamic import 404s. The chunk-error
 * handler in `public/chunk-error-handler.js` notices the rejection and
 * schedules a cache-busting reload, but in the meantime React's vanilla
 * `lazy()` stores `{ _result: undefined }` and the next render throws
 * `TypeError: Cannot read properties of undefined (reading 'default')`
 * (Safari: `undefined is not an object (evaluating 'f._result.default')`).
 * That render error then bubbles to the Sentry ErrorBoundary, masking the
 * real cause (stale deployment) and creating P1 noise on every release.
 *
 * `lazyWithReload` short-circuits this race: on a recognised chunk-load
 * failure we trigger a hard reload immediately and return a
 * never-resolving promise so React keeps showing the Suspense fallback
 * until the navigation tears down the page. Any other rejection (real
 * module-level bug) propagates so the ErrorBoundary still catches it.
 *
 * Drop-in replacement for `React.lazy` — same call shape, same return type.
 */

const CHUNK_LOAD_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'Loading chunk',
  'Unable to preload CSS for',
];

const SESSION_FLAG = 'lazyWithReload:attempted';

function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const msg = err instanceof Error ? err.message : String(err);
  return CHUNK_LOAD_PATTERNS.some((p) => msg.includes(p));
}

function triggerCacheBustReload(): void {
  try {
    if (sessionStorage.getItem(SESSION_FLAG) === '1') return;
    sessionStorage.setItem(SESSION_FLAG, '1');
  } catch {
    // sessionStorage unavailable (Safari private mode); reload anyway.
  }
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('_t', String(Date.now()));
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
}

export function lazyWithReload<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return React.lazy(() =>
    factory().catch((error) => {
      if (isChunkLoadError(error)) {
        triggerCacheBustReload();
        return new Promise<{ default: T }>(() => {});
      }
      throw error;
    }),
  );
}
